// src/client/providers/AuthProvider.tsx
//
// Phase B T2a/T2c: AuthContext + boot-time `/api/v1/auth/me` 同期.
//
// T2a (skeleton): User 型 + Context + Provider + useAuth() hook
// T2c (本 sub-step): boot 時に sessionStorage に token があれば
//   - `/api/v1/auth/me` を fetch して user (role 含む) を refresh
//   - 401 等は sessionStorage を clear
//   - network error 時は stored user を fallback として保持 (offline UX)
//   - `isInitializing` を ProtectedRoute が参照
// #701: logout() は server の POST /api/v1/auth/logout で今の token を失効させてから
//   保存した session を消す。 通信失敗 / offline / timeout でも **必ず消す** (best-effort)。
// #701: lib/api.ts が session 無効の 401 を検知すると SESSION_INVALID_EVENT を投げる。 ここで未 login に戻し、
//   理由 (sessionNotice) を保持する → ProtectedRoute が login へ送り、 Login が理由を表示する。
//
// 参照:
//  - src/server/routes/auth.ts の `GET /me` handler (/me response shape の正本)
//  - docs/plans/phase-b-frontend-mvp-plan.md Q2 (sessionStorage MVP 暫定)

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  ApiNetworkError,
  getJson,
  postJson,
  SESSION_INVALID_EVENT,
  type SessionNotice,
} from "../lib/api";
import { clearAuth, loadAuth, saveAuth } from "../lib/session-storage";

// ---------------------------------------------------------------------------
// Types — `/api/v1/auth/me` response data shape
// ---------------------------------------------------------------------------

export type AuthUserRole = {
  readonly roleId: string;
  readonly roleKey: string;
  readonly archetype: string;
};

export type AuthUser = {
  readonly userId: string;
  readonly organizationId: string;
  readonly displayName: string;
  readonly email: string | null;
  readonly locale: string | null;
  readonly role: AuthUserRole | null;
  /**
   * Phase 8.5 audit P-D: tenant_admin capability の薄い surface.
   * 完全な permissionJson は client に出さない (server で gate enforce 済)、
   * 本 flag は admin nav link 表示判断のみで使用.
   * 未設定 (古い token) や role なし時は undefined / false.
   */
  readonly tenantAdmin?: boolean;
};

// ---------------------------------------------------------------------------
// Context value
// ---------------------------------------------------------------------------

export type AuthContextValue = {
  readonly user: AuthUser | null;
  readonly isAuthenticated: boolean;
  /** boot 時に sessionStorage の token から `/me` を fetch している間 true。完了で false。 */
  readonly isInitializing: boolean;
  /**
   * boot fetch /me が **真の network 不通** で失敗し、stored user を fallback として使っている状態。
   * - 401/403 (無効 token) では false (このとき clear される)
   * - true の間は write 系 mutation hook 側で disable / pending 表示にする (Phase B+ T3 以降)
   */
  readonly isStale: boolean;
  /** T2b: login 成功時 / T2c: `/me` 反映時に呼ぶ。sessionStorage の user 部分も更新。 */
  readonly setUser: (user: AuthUser | null) => void;
  /** sessionStorage を全 key clear して未認証状態に戻す (server は呼ばない)。 */
  readonly clear: () => void;
  /**
   * #701: server で今の token を失効させてから clear する。 server が失敗 / 応答しなくても clear は必ず行う
   * (待つのは最大 {@link LOGOUT_TIMEOUT_MS})。
   */
  readonly logout: () => Promise<void>;
  /** #701: 別の端末での PW 変更などで session が無効になった理由 (login 画面に表示)。 login すると消える */
  readonly sessionNotice: SessionNotice | null;
};

const SESSION_NOTICES: readonly SessionNotice[] = ["sessionRevoked", "sessionInactive", "sessionExpired"];

/** logout の server 呼び出しを待つ上限。 offline で画面が固まらないよう短くする。 */
const LOGOUT_TIMEOUT_MS = 3000;

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

type AuthProviderProps = {
  readonly children: ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  // 初期 user: sessionStorage から復元 (boot fetch までの間も認証済 UI を出せる)
  const [user, setUserState] = useState<AuthUser | null>(() => loadAuth()?.user ?? null);
  // boot 時に token があれば true でスタート、なければ即 false
  const [isInitializing, setIsInitializing] = useState<boolean>(() => loadAuth()?.token != null);
  // boot fetch が真の network 不通で失敗 → stored user fallback を使っている間 true
  const [isStale, setIsStale] = useState<boolean>(false);

  // boot 時 1 回だけ `/me` を fetch して user を refresh
  useEffect(() => {
    const stored = loadAuth();
    if (!stored?.token) {
      setIsInitializing(false);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    // 401/403 を受けたときの defensive clear (両経路で共通呼出)。
    const clearOnInvalidToken = (): void => {
      clearAuth();
      setUserState(null);
      setIsStale(false);
    };

    (async () => {
      try {
        const res = await getJson<AuthUser>("/api/v1/auth/me", {
          signal: controller.signal,
        });
        if (cancelled) return;
        if (res.ok) {
          // role / archetype / locale 含む完全な user で上書き
          setUserState(res.data);
          setIsStale(false);
          // sessionStorage の user も最新化 (token / expiresAt は保持)
          saveAuth({
            token: stored.token,
            expiresAt: stored.expiresAt,
            user: res.data,
          });
        } else {
          // 401/403 等 — token 無効化
          clearOnInvalidToken();
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiNetworkError) {
          // T2c.5 Test 2 fix: response が返って来ているのに parse / shape で
          // ApiNetworkError 化したケースで status を見て 401/403 なら clear。
          if (err.status === 401 || err.status === 403) {
            clearOnInvalidToken();
          } else {
            // 真の network 不通 (status undefined) または 5xx 等:
            // stored user を fallback として保持し isStale = true (write は Phase B+ T3 以降で respect)
            setIsStale(true);
          }
        } else {
          // 想定外: 安全側に倒して clear
          clearOnInvalidToken();
        }
      } finally {
        if (!cancelled) setIsInitializing(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // #701: lib/api.ts からの 「session が無効になった」 通知 (保存した session は api.ts が消し済み)
  const [sessionNotice, setSessionNotice] = useState<SessionNotice | null>(null);
  useEffect(() => {
    const onInvalid = (e: Event): void => {
      const n = (e as CustomEvent<{ notice?: unknown }>).detail?.notice;
      setUserState(null);
      setIsStale(false);
      setSessionNotice(SESSION_NOTICES.includes(n as SessionNotice) ? (n as SessionNotice) : "sessionExpired");
    };
    window.addEventListener(SESSION_INVALID_EVENT, onInvalid);
    return () => window.removeEventListener(SESSION_INVALID_EVENT, onInvalid);
  }, []);

  const setUser = useCallback((next: AuthUser | null) => {
    if (next) setSessionNotice(null); // login し直したら理由は消す
    setUserState(next);
    // login 成功時等で setUser(non-null) されたら fresh 扱い、setUser(null) 時もリセット
    setIsStale(false);
  }, []);

  // 2026-05-09 flicker fix v3 (login 後 /auth/me re-fetch trigger):
  // AuthProvider boot fetch (上の useEffect) は mount 時 1 回のみ. incognito で
  // /login → AuthProvider mount (token なし → fetch スキップ) → login → token 保存
  // でも AuthProvider 再 mount しない → /auth/me 永遠に走らない → user.role 永遠 null.
  //
  // 修正: user が non-null かつ role 未 populate な時、 /auth/me を fetch して role を埋める.
  // sessionStorage の user (login response) も同経路で更新.
  useEffect(() => {
    if (!user) return;
    if (user.role?.archetype) return; // role 既 populate なら何もしない
    const stored = loadAuth();
    if (!stored?.token) return;
    const controller = new AbortController();
    let cancelled = false;
    (async () => {
      try {
        const res = await getJson<AuthUser>("/api/v1/auth/me", {
          signal: controller.signal,
        });
        if (cancelled) return;
        if (res.ok) {
          setUserState(res.data);
          setIsStale(false);
          saveAuth({
            token: stored.token,
            expiresAt: stored.expiresAt,
            user: res.data,
          });
        }
      } catch {
        // network error: 静かに諦め (boot fetch の isStale 経路と区別、 login 直後なので skeleton で十分)
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
    // user が変化した時のみ再 fetch trigger
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId]);

  const clear = useCallback(() => {
    clearAuth();
    setUserState(null);
    setIsStale(false);
  }, []);

  const logout = useCallback(async () => {
    if (loadAuth()?.token) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), LOGOUT_TIMEOUT_MS);
      try {
        // 失敗しても続行する (token は端末から消える。 server の失効は best-effort)
        await postJson<{ message: string }>("/api/v1/auth/logout", {}, { signal: controller.signal });
      } catch {
        // offline / timeout / 5xx — clear は下で必ず行う
      } finally {
        clearTimeout(timer);
      }
    }
    clear();
    // 自分でログアウトした (失効済み token で /logout が 401 になっても) ときは理由を出さない
    setSessionNotice(null);
  }, [clear]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isInitializing,
      isStale,
      setUser,
      clear,
      logout,
      sessionNotice,
    }),
    [user, isInitializing, isStale, setUser, clear, logout, sessionNotice],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
